import { useEffect, useState } from "react"
import { Alert } from "antd"
import _ from "lodash"
import moment from "moment-timezone"
import { getExpectedDeliveryDate } from "modules/order_management/api_layer/api"
import { CATEGORY } from "modules/my_tickets/data"

// Keeps Create Ticket disabled while the order is still within its EDD. The footer disables the
// button whenever any key in `validation` is falsy, so owning one key is the whole enforcement.
const VALIDATION_KEY = "edd_guardrail"

// Gates on the subcategory alone. The parent category is spelled three different ways across the
// ticketing config (delivery_related, delivery_Issues, delivery_related_issues) and the rows are
// edited at runtime in Mongo, so an AND on the category risks the guardrail never firing at all.
// The label fallback covers a key that gets renamed in Ticket Configure.
export const isOrderDelayedSubcategory = (value, label) =>
    value === CATEGORY.ORDER_DELAYED ||
    String(label || "").trim().toLowerCase() === "order delayed"

const STATUS = {
    LOADING: "loading",
    WITHIN_EDD: "within_edd",
    BREACHED: "breached",
    UNAVAILABLE: "unavailable",
}

const EddGuardrail = ({ orderDisplayId, setValidation, onBlockedChange }) => {
    const [state, setState] = useState({ status: STATUS.LOADING, edd: null })

    useEffect(() => {
        // A stale response must never flip the gate for an order the agent has already moved off.
        let cancelled = false

        const settle = (status, edd) => {
            if (cancelled) return
            setState({ status, edd })
            const allowed = status === STATUS.BREACHED || status === STATUS.UNAVAILABLE
            setValidation((prev) => ({ ...prev, [VALIDATION_KEY]: allowed }))
            // Only a confirmed within-EDD order hides the rest of the form. While the date is still
            // loading the button is disabled but the form stays put, so it does not flicker.
            onBlockedChange?.(status === STATUS.WITHIN_EDD)
        }

        setState({ status: STATUS.LOADING, edd: null })
        setValidation((prev) => ({ ...prev, [VALIDATION_KEY]: false }))
        onBlockedChange?.(false)

        if (!orderDisplayId) {
            settle(STATUS.UNAVAILABLE, null)
        } else {
            // orderDisplayId goes through verbatim, '#' included: the internal API resolves the order
            // with orderDisplayId.split('#')[1], so a stripped id looks up `undefined` upstream.
            getExpectedDeliveryDate(orderDisplayId)
                .then((response) => {
                    // The endpoint answers with a bare ISO string, not an object.
                    const raw = _.get(response, ["data"], null)
                    const edd = raw ? moment(raw) : null
                    if (!edd || !edd.isValid()) return settle(STATUS.UNAVAILABLE, null)
                    settle(edd.isBefore(moment()) ? STATUS.BREACHED : STATUS.WITHIN_EDD, raw)
                })
                // Fail open: an API blip must not trap a genuinely delayed order behind the guardrail.
                .catch(() => settle(STATUS.UNAVAILABLE, null))
        }

        return () => {
            cancelled = true
            // The key has to go, not just flip to true — a leftover key would keep judging
            // subcategories this guardrail knows nothing about.
            setValidation((prev) => {
                const { [VALIDATION_KEY]: removed, ...rest } = prev
                return rest
            })
            onBlockedChange?.(false)
        }
    }, [orderDisplayId])

    // Same rendering as the actionables truck icon, so both surfaces always read the same date.
    const eddLabel = state.edd ? moment.tz(state.edd, "Asia/Kolkata").format("MMM DD YYYY") : null

    if (state.status === STATUS.LOADING) {
        return <Alert type="info" message="Checking the expected delivery date…" showIcon className="my-2" />
    }

    if (state.status === STATUS.WITHIN_EDD) {
        return (
            <Alert
                type="error"
                message="No need to raise this ticket"
                description={`This order is still within its Expected Delivery Date (${eddLabel}). Share this date with the customer — an Order Delayed ticket can be raised only once the EDD is breached.`}
                showIcon
                className="my-2"
            />
        )
    }

    if (state.status === STATUS.BREACHED) {
        return (
            <Alert
                type="warning"
                message="EDD breached — you can raise this ticket"
                description={`The Expected Delivery Date (${eddLabel}) has passed.`}
                showIcon
                className="my-2"
            />
        )
    }

    return (
        <Alert
            type="warning"
            message="Expected delivery date unavailable"
            description="The EDD could not be fetched for this order. Raise the ticket only if the order is genuinely delayed."
            showIcon
            className="my-2"
        />
    )
}

export default EddGuardrail
